import {
  Flex,
  HStack,
  IconButton,
  Portal,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import html2pdf from 'html2pdf.js';
import { toPng } from 'html-to-image';
import { useEffect, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector } from '@transport-insights/uikit';
import { useIsFetching } from 'react-query';
import { selectedRegionRoadTypeState } from '../state';
import { roadTypeOptions } from '../shared/constants';

function PrintablePdf({
  printableRef, pageTitle = null, fileName = null, printableStyles = null,
}) {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const [isLoaded, setIsLoaded] = useState(false);
  const isFetching = useIsFetching();
  const selectedRegionRoadType = useRecoilValue(selectedRegionRoadTypeState);
  const regionRoadTypeLabel = roadTypeOptions.find((option) => option.value === selectedRegionRoadType)?.label;

  // Check if all queries have loaded
  // Delay the loading state to avoid flickering
  useEffect(() => {
    setIsLoaded(false);
    setTimeout(() => {
      if (isFetching === 0) {
        setIsLoaded(true);
      }
    }, 1000);
  }, [isFetching, currentDatabase]);

  const handlePrint = async () => {
    setIsGeneratingPdf(true);

    setTimeout(async () => {
    // Clone the content of the reference
      const printableContent = printableRef.current.cloneNode(true);

      // Set the scale factor based on the device pixel ratio
      const widthScaleFactor = window.devicePixelRatio > 1 ? 0.35 : 0.7;
      const heightScaleFactor = window.devicePixelRatio > 1 ? 0.3 : 0.6;

      // Replace each canvas element with an image version
      const originalCanvases = Array.from(printableRef.current?.querySelectorAll('canvas'));
      const clonedCanvases = Array.from(printableContent?.querySelectorAll('canvas'));

      originalCanvases?.forEach((canvas, index) => {
        const initialWidth = canvas.width;
        const initialHeight = canvas.height;
        const img = document.createElement('img');
        img.src = canvas.toDataURL('image/png');

        if (initialWidth > initialHeight) {
          img.style.maxWidth = `${initialWidth * widthScaleFactor}px`;
          img.style.maxHeight = 'none';
          img.style.height = 'auto';
        } else {
          img.style.maxHeight = `${initialHeight * heightScaleFactor}px`;
          img.style.maxWidth = 'none';
          img.style.width = 'auto';
        }

        const clonedCanvas = clonedCanvases?.[index];
        if (clonedCanvas && clonedCanvas.parentNode) {
          clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
        }
      });

      /*
        Apex Charts
      */

      // Set the scale factor based on the device pixel ratio
      const apexWidthScaleFactor = window.devicePixelRatio > 1 ? 0.55 : 0.65;
      const apexHeightScaleFactor = window.devicePixelRatio > 1 ? 0.5 : 0.6;

      // Replace each ApexChart with an image version
      const originalApexCharts = Array.from(printableRef.current?.getElementsByClassName('apexcharts-canvas'));
      const clonedApexCharts = Array.from(printableContent?.getElementsByClassName('apexcharts-canvas'));

      const chartReplacementPromises = originalApexCharts?.map(async (chart, index) => {
        const initialWidth = chart.offsetWidth;
        const initialHeight = chart.offsetHeight;

        // Remove the apexchart toolbar
        const toolbarEl = chart.querySelector('.apexcharts-toolbar');
        if (toolbarEl) {
          toolbarEl.style.display = 'none';
        }

        // Hide interactive elements (crosshair, tooltip, etc.)
        const tooltipEl = chart.querySelector('.apexcharts-tooltip');
        const crosshairs = chart.querySelectorAll('.apexcharts-crosshairs, .apexcharts-xcrosshairs, .apexcharts-ycrosshairs');

        if (tooltipEl) tooltipEl.style.display = 'none';
        crosshairs.forEach((el) => { el.style.display = 'none'; });

        const chartSvg = chart.children[0];
        const chartLegend = chart.children[1];

        try {
          // Move the legend into the SVG for rendering
          // This is a fix for Apexcharts v5 where the legend is no longer part of the SVG
          const foreignObject = chartSvg.querySelector('foreignObject');
          foreignObject.appendChild(chartLegend);

          const dataUrl = await toPng(chartSvg, {
            pixelRatio: 4,
            cacheBust: true,
          });

          const img = document.createElement('img');
          img.src = dataUrl;

          if (initialWidth > initialHeight) {
            img.style.width = `${initialWidth * apexWidthScaleFactor}px`;
            img.style.height = 'auto';
          } else {
            img.style.height = `${initialHeight * apexHeightScaleFactor}px`;
            img.style.width = 'auto';
          }

          const clonedDiv = clonedApexCharts[index];
          if (clonedDiv && clonedDiv.parentNode) {
            clonedDiv.parentNode.style.cssText = '';
            clonedDiv.parentNode.replaceChild(img, clonedDiv);
          }
        } catch (error) {
          console.error(`Error rendering ApexChart at index ${index}:`, error);
        }

        // Show interactive elements (crosshair, tooltip, etc.)
        if (tooltipEl) tooltipEl.style.display = '';
        crosshairs.forEach((el) => { el.style.display = ''; });
        if (toolbarEl) {
          toolbarEl.style.display = '';
        }
      });

      // Wait for all chart replacements to finish
      await Promise.all(chartReplacementPromises);

      // Extract styles, replacing CSS variables if necessary
      let styles = '';
      Array.from(document.styleSheets).forEach((styleSheet) => {
        try {
          Array.from(styleSheet.cssRules).forEach((rule) => {
            styles += rule.cssText;
          });
        } catch (e) {
          console.warn('Could not access stylesheet:', styleSheet.href);
        }
      });

      // Set up the HTML content with styles
      const finalHtml = `
      <html>
        <head>
          <title>Print</title>
          <style>
            ${styles}
            @page {
              size: A4 landscape;
            }
            body, html {
              font-family: Arial, sans-serif;
              width: 100%;
            }
            img {
              height: unset;
              display: inline-block !important;
              max-width: 100%;
            }
            .pdf-container {
              margin: auto;
              padding: 4mm;
              overflow: hidden;
            }
            .pdf-container .hidden-print,
            .pdf-container .apexcharts-toolbar { 
              display: none!important;
            }
            .pdf-container .lockyear-badge {
              font-size: 8px;
            }
            .pdf-container .quarter-badge {
              font-size: 8px;
            }
            .pdf-container .report-header {
              top: 0px;
              padding: 0px!important;
              margin-bottom: 0.5mm;
              background: #fff;
              line-height: 1.2;
            }
            .pdf-container .report-header .report-title {
              font-size: 18px;
            }
            .pdf-container .report-header .report-subtitle {
              font-size: 12px;
            }
            .pdf-container .pdf-half {
              width: 45%;
            }
            .pdf-container .report-section-header {
              font-size: 11px;
              margin: 0px 0px 4px 0px;
            }
            .pdf-container .report-section-header p {
              font-size: 11px;
            }
            .pdf-container .chart-title {
              font-size: 8px;
              margin-bottom: 0.5mm;
              line-height: 1.1;
            }
            .pdf-container .dashboard-grid {
              display: grid;
              width: auto;
              grid-template-columns: repeat(6, 1fr);
              gap: 1mm;
              margin: 0px;
            }
            .pdf-container .indicator {
              transform: scale(0.75);
              padding: 0px;
            }
            .pdf-container .widget-container,
            .pdf-container .report-section-container {
              border: 1px solid #d5d5d5;
              border-radius: 4px;
              background-color: #fff;
              box-shadow: none;
              height: unset;
              padding: 4px;
            }
            .pdf-container .report-section-container {
              flex-grow: 1;
            }
            .pdf-container .widget-header {
              padding: 4px 6px 0px 6px;
            }
            .pdf-container .widget-header .widget-title {
              font-size: 10px;
            }
            .pdf-container .widget-body {
              min-height: unset;
              max-height: 100px;
            }
            .pdf-container .widget-description > p,
            .pdf-container .grouped-region-message {
              font-size: 8px;
            }
            .pdf-container .widget-footer {
              padding: 3px 6px 6px 6px;
            }
            .pdf-container .performance-stats {
              padding: 6px;
              border: 1px solid #ddd;
              border-radius: 4px;
              background-color: #fff;
              box-shadow: none;
              width: auto;
              margin: 5px 0px 0px 0px;
            }
            .pdf-container .stat-label {
              font-size: 10px;
            }
            .pdf-container .stat-value {
              font-size: 16px;
            }
            .pdf-container .legend-label {
              font-size: 8px;
            }
            .pdf-container .legend-indicator {
              width: 6px;
              height: 6px;
            }
            .pdf-container .report-source-text {
              font-size: 8px;
            }
            .pdf-container table th  {
              font-size: 9px!important;
              padding: 2px 6px!important;
            }
            .pdf-container table td {
              padding: 2px 6px!important;
            }
            .pdf-container table p {
              font-size: 9px!important;
            }
            ${printableStyles}
          </style>
        </head>
        <body>
          <div class="pdf-container">
            <header style="display: flex; align-items: center; justify-content: space-between; width: 100%; padding-bottom: 0; margin-bottom: 0.5mm;">
            <h2 style="line-height: 0.5; font-size: 16px; font-weight: normal; padding: 0px">
              ${pageTitle
    ? `${pageTitle} - ${currentDatabase?.name}${currentDatabase?.id >= 1000 ? ` (${regionRoadTypeLabel})` : ''}`
    : `${currentDatabase?.name}${currentDatabase?.id >= 1000 ? ` (${regionRoadTypeLabel})` : ''}`}
            </h2>             
            <img src="/images/reg-logo-print.jpg" width="90px" height="auto" />
            </header>
            <div>
              ${printableContent.innerHTML}
            </div>
          </div>
        </body>
      </html>
    `;

      // PDF options
      const options = {
        margin: 0,
        image: { type: 'jpeg', quality: 0.98 },
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-after',
          after: '.page-break-before',
          avoid: '.avoid-page-break',
        },
        html2canvas: {
          scale: 4,
          width: 1123,
          useCORS: true,
          allowTaint: true,
        },
        jsPDF: {
          unit: 'mm', format: 'a4', orientation: 'landscape', putTotalPages: true,
        },
      };

      // Generate and save PDF
      await html2pdf().set(options)
        .from(finalHtml)
        .toContainer()
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            pdf.setFontSize(7);
            pdf.setTextColor(100);
            pdf.text(`Page ${i} of ${totalPages}`, pdf.internal.pageSize.getWidth() - 5, pdf.internal.pageSize.getHeight() - 5, 'right');
            pdf.text(`Generated on ${new Date().toLocaleString('en-NZ')}`, 5, pdf.internal.pageSize.getHeight() - 5, 'left');
          }
          pdf.save(`${fileName} - ${currentDatabase?.name}.pdf`);
        });
      setIsGeneratingPdf(false);
    }, 50);
  };

  return (
    <>
      {isGeneratingPdf && (
      <Portal>
        <Flex
          zIndex={9999}
          position="fixed"
          top={0}
          bottom={0}
          left="0"
          right={0}
          width="100vw"
          height="100vh"
          background="whiteAlpha.800"
          align="center"
          justify="center"
          backdropFilter="blur(4px)"
          marginInlineStart="0!important"
        >
          <HStack p="0" m="0">
            <Spinner color="brand.orange.500" size="lg" />
            <Text>Generating PDF...</Text>
          </HStack>
        </Flex>
      </Portal>
      )}
      <Tooltip
        label="Save as PDF"
        placement="left"
        display={isGeneratingPdf ? 'none' : 'inherit'}
      >
        <IconButton
          icon={<FiDownload />}
          borderRadius="full"
          colorScheme="blue"
          variant="outline"
          ml={3}
          onClick={handlePrint}
          isDisabled={!isLoaded}
        />
      </Tooltip>
    </>
  );
}

export default PrintablePdf;
